import React, { useState, useEffect, useRef } from 'react';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import {
  FaRocket,
  FaStar,
  FaCheckCircle,
  FaTimesCircle,
  FaWallet,
  FaTwitter,
  FaDiscord,
  FaTelegram,
  FaGlobe,
  FaChevronDown,
  FaEllipsisH,
  FaTimes,
  FaHeart,
  FaShareAlt,
  FaFlag,
  FaLock,
  FaClock,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaPlus,
  FaMinus
} from 'react-icons/fa';
import { getSubmissions } from '../services/firebase';
import '../styles/DropsPage.css';

// Helper function to load approved drops from Firebase (with localStorage fallback)
const loadApprovedDrops = async () => {
  try {
    const submissions = await getSubmissions();
    const approvedDrops = submissions.filter(
      sub => sub.service === 'drops_listing' && sub.status === 'approved'
    );

    return approvedDrops.map(sub => {
      // Parse drop date (format: "2024-03-15 18:00 UTC")
      let startTime = Math.floor(Date.now() / 1000);
      let dropStatus = 'live';

      if (sub.materials.dropDate) {
        const parsedDate = new Date(sub.materials.dropDate.replace(' UTC', 'Z'));
        if (!isNaN(parsedDate.getTime())) {
          startTime = Math.floor(parsedDate.getTime() / 1000);
          if (startTime > Math.floor(Date.now() / 1000)) {
            dropStatus = 'upcoming';
          }
        }
      }

      // Parse supply (e.g., "10,000 NFTs" -> 10000)
      const supplyStr = sub.materials.supply || '1000';
      const maxSupply = parseInt(supplyStr.replace(/[^0-9]/g, '')) || 1000;

      // Parse price (e.g., "500 KLV" -> 500)
      const priceStr = sub.materials.mintPrice || '100 KLV';
      const priceMatch = priceStr.match(/(\d+)/);
      const pricePerNft = priceMatch ? parseInt(priceMatch[1]) : 100;

      return {
        id: sub.id,
        name: sub.materials.collection ? sub.materials.collection.split('-')[0] : 'Unknown Collection',
        ticker: sub.materials.collection || 'UNKNOWN',
        assetId: sub.materials.collection || 'UNKNOWN',
        description: sub.materials.description || 'No description provided.',
        bannerImage: sub.images.banner?.data || 'https://via.placeholder.com/1400x400',
        logoImage: sub.images.logo?.data || 'https://via.placeholder.com/400x400',
        featuredImage: sub.images.logo?.data || 'https://via.placeholder.com/800x800',
        maxSupply: maxSupply,
        mintedAmount: 0,
        pricePerNft: pricePerNft,
        currency: 'KLV',
        precision: 6,
        startTime: startTime,
        endTime: startTime + (30 * 86400), // 30 days from start
        whitelistEnabled: false,
        stages: [
          { name: 'Public', price: pricePerNft, startTime: startTime, maxPerWallet: 10 }
        ],
        website: sub.materials.website || '',
        twitter: sub.materials.twitter || '',
        telegram: sub.materials.telegram || '',
        creator: {
          name: sub.wallet ? `${sub.wallet.slice(0, 8)}...${sub.wallet.slice(-4)}` : 'Unknown',
          address: sub.wallet || '',
          verified: false,
          image: sub.images.logo?.data || 'https://via.placeholder.com/100x100'
        },
        stats: {
          totalVolume: '--',
          floorPrice: '--',
          owners: 0
        },
        allowlistEnabled: false,
        status: dropStatus,
        isFromSubmission: true
      };
    });
  } catch (error) {
    console.error('Error loading approved drops:', error);
    return [];
  }
};

// Demo drops (shown when no approved submissions exist)
const DEMO_DROPS = [
  {
    id: 'demo-001',
    name: 'Cosmic Legends',
    ticker: 'COSMIC-1X2Y',
    assetId: 'COSMIC-1X2Y',
    description: 'A collection of 10,000 unique cosmic beings from across the multiverse. Each Legend possesses rare traits and powers that define their cosmic heritage.',
    bannerImage: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1400&h=400&fit=crop',
    logoImage: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=400&h=400&fit=crop',
    featuredImage: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=800&h=800&fit=crop',
    maxSupply: 10000,
    mintedAmount: 3247,
    pricePerNft: 500,
    currency: 'KLV',
    precision: 6,
    startTime: Math.floor(Date.now() / 1000) - 86400,
    endTime: Math.floor(Date.now() / 1000) + (7 * 86400),
    whitelistEnabled: false,
    stages: [{ name: 'Public', price: 500, startTime: Math.floor(Date.now() / 1000) - 86400, maxPerWallet: 10 }],
    website: 'https://cosmiclegends.io',
    twitter: 'https://twitter.com/cosmiclegends',
    discord: 'https://discord.gg/cosmiclegends',
    creator: { name: 'Cosmic Studios', address: 'klv1cosmic...studios', verified: true, image: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=100&h=100&fit=crop' },
    stats: { totalVolume: '2.5M KLV', floorPrice: '650 KLV', owners: 1847 },
    allowlistEnabled: false,
    status: 'live',
    isDemo: true
  }
];

const DropsPage = () => {
  const { isConnected, walletAddress, connectWallet, kdaFee } = useWallet();

  const [drops, setDrops] = useState([]);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [mintAmount, setMintAmount] = useState(1);
  const [userBalance, setUserBalance] = useState(0);
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));
  const [loading, setLoading] = useState(true);
  const [transactionState, setTransactionState] = useState('');
  const [transactionMessage, setTransactionMessage] = useState('');
  const [activeTab, setActiveTab] = useState('live'); // 'live', 'upcoming', 'past'

  const modalRef = useRef(null);

  // Load drops from Firebase (approved submissions) + demo drops
  useEffect(() => {
    const fetchDrops = async () => {
      const approvedDrops = await loadApprovedDrops();
      // Show approved drops first, then demo drops if none exist
      if (approvedDrops.length > 0) {
        setDrops(approvedDrops);
      } else {
        // Show demo drops when no approved submissions exist
        setDrops(DEMO_DROPS);
      }
    };
    fetchDrops();
  }, []);

  // Update time
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setTimeout(() => setLoading(false), 800);
  }, []);

  // Fetch balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!isConnected || !walletAddress || !selectedDrop) return;
      try {
        const response = await fetch(`https://api.mainnet.klever.org/v1.0/address/${walletAddress}`);
        const data = await response.json();
        const klvBalance = data.data?.account?.balance || 0;
        setUserBalance(klvBalance / Math.pow(10, 6));
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchBalance();
  }, [isConnected, walletAddress, selectedDrop]);

  // Close modal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
      }
    };
    if (selectedDrop) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedDrop]);

  // Format countdown
  const formatCountdown = (targetTime) => {
    const diff = targetTime - currentTime;
    if (diff <= 0) return null;

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    return { days, hours, minutes, seconds };
  };

  // Get drop status
  const getDropStatus = (drop) => {
    const now = currentTime;

    if (drop.mintedAmount >= drop.maxSupply) {
      return { status: 'minted', label: 'Minted Out' };
    }
    if (drop.endTime && now > drop.endTime) {
      return { status: 'ended', label: 'Ended' };
    }
    if (drop.startTime && now < drop.startTime) {
      return { status: 'upcoming', label: 'Upcoming', countdown: formatCountdown(drop.startTime) };
    }

    // Check allowlist phase
    if (drop.whitelistEnabled && drop.whitelistEndTime && now < drop.whitelistEndTime) {
      return { status: 'allowlist', label: 'Allowlist', countdown: formatCountdown(drop.whitelistEndTime) };
    }

    return { status: 'live', label: 'Live', countdown: drop.endTime ? formatCountdown(drop.endTime) : null };
  };

  // Filter drops by tab
  const filteredDrops = drops.filter(drop => {
    const status = getDropStatus(drop);
    if (activeTab === 'live') return status.status === 'live' || status.status === 'allowlist';
    if (activeTab === 'upcoming') return status.status === 'upcoming';
    if (activeTab === 'past') return status.status === 'ended' || status.status === 'minted';
    return true;
  });

  // Handle mint
  const handleMint = async () => {
    if (!isConnected || !selectedDrop) return;

    setTransactionState('processing');
    setTransactionMessage('Preparing transaction...');

    try {
      const contract = {
        type: 17,
        payload: {
          buyType: 0,
          id: selectedDrop.assetId,
          currencyId: selectedDrop.currency,
          amount: mintAmount
        }
      };

      setTransactionMessage('Please confirm in your wallet...');
      const unsignedTx = await web.buildTransaction([contract], undefined, kdaFee);
      const signedTx = await web.signTransaction(unsignedTx);

      setTransactionMessage('Broadcasting...');
      const response = await web.broadcastTransactions([signedTx]);

      if (response.error) throw new Error(response.error);

      setTransactionState('success');
      setTransactionMessage(`Successfully minted ${mintAmount} NFT${mintAmount > 1 ? 's' : ''}!`);

      setTimeout(() => closeModal(), 3000);
    } catch (error) {
      setTransactionState('error');
      setTransactionMessage(error.message || 'Transaction failed');
    }
  };

  const openModal = (drop) => {
    setSelectedDrop(drop);
    setMintAmount(1);
    setTransactionState('');
    setTransactionMessage('');
  };

  const closeModal = () => {
    if (transactionState === 'processing') return;
    setSelectedDrop(null);
  };

  const getTotalPrice = () => {
    if (!selectedDrop) return 0;
    return selectedDrop.pricePerNft * mintAmount;
  };

  const getProgress = (drop) => {
    return (drop.mintedAmount / drop.maxSupply) * 100;
  };

  // Render countdown display
  const renderCountdown = (countdown) => {
    if (!countdown) return null;
    return (
      <div className="os-countdown">
        <div className="os-countdown-block">
          <span className="os-countdown-value">{String(countdown.days).padStart(2, '0')}</span>
          <span className="os-countdown-label">Days</span>
        </div>
        <span className="os-countdown-sep">:</span>
        <div className="os-countdown-block">
          <span className="os-countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
          <span className="os-countdown-label">Hrs</span>
        </div>
        <span className="os-countdown-sep">:</span>
        <div className="os-countdown-block">
          <span className="os-countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
          <span className="os-countdown-label">Min</span>
        </div>
        <span className="os-countdown-sep">:</span>
        <div className="os-countdown-block">
          <span className="os-countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
          <span className="os-countdown-label">Sec</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="os-drops-page">
        <div className="os-loading">
          <div className="os-loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="os-drops-page">
      {/* Header */}
      <div className="os-drops-header">
        <h1>Drops</h1>
        <p>Discover and collect exclusive NFT drops on KleverChain</p>
      </div>

      {/* Tabs */}
      <div className="os-drops-tabs">
        <button
          className={`os-tab ${activeTab === 'live' ? 'active' : ''}`}
          onClick={() => setActiveTab('live')}
        >
          Live & Upcoming
        </button>
        <button
          className={`os-tab ${activeTab === 'past' ? 'active' : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>

      {/* Drops List */}
      <div className="os-drops-container">
        {filteredDrops.length > 0 ? (
          <div className="os-drops-list">
            {filteredDrops.map((drop) => {
              const status = getDropStatus(drop);
              const progress = getProgress(drop);

              return (
                <div
                  key={drop.id}
                  className={`os-drop-card ${status.status}`}
                  onClick={() => openModal(drop)}
                >
                  {/* Banner */}
                  <div className="os-drop-banner">
                    <img src={drop.bannerImage} alt="" />
                    <div className="os-drop-banner-overlay"></div>

                    {/* Status Badge */}
                    <div className={`os-status-badge ${status.status}`}>
                      {status.status === 'live' && <span className="os-live-dot"></span>}
                      {status.label}
                    </div>

                    {/* Logo */}
                    <div className="os-drop-logo">
                      <img src={drop.logoImage} alt={drop.name} />
                      {drop.creator.verified && (
                        <div className="os-verified">
                          <FaCheckCircle />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="os-drop-content">
                    <div className="os-drop-main">
                      <h3 className="os-drop-name">{drop.name}</h3>
                      <p className="os-drop-creator">
                        by <span>{drop.creator.name}</span>
                      </p>
                    </div>

                    {/* Countdown or Stats */}
                    {status.countdown ? (
                      <div className="os-drop-countdown-section">
                        <span className="os-countdown-title">
                          {status.status === 'upcoming' ? 'Starts in' :
                           status.status === 'allowlist' ? 'Allowlist ends in' : 'Ends in'}
                        </span>
                        {renderCountdown(status.countdown)}
                      </div>
                    ) : status.status === 'minted' || status.status === 'ended' ? (
                      <div className="os-drop-stats-row">
                        <div className="os-stat">
                          <span className="os-stat-label">Floor</span>
                          <span className="os-stat-value">{drop.stats.floorPrice}</span>
                        </div>
                        <div className="os-stat">
                          <span className="os-stat-label">Volume</span>
                          <span className="os-stat-value">{drop.stats.totalVolume}</span>
                        </div>
                      </div>
                    ) : null}

                    {/* Progress */}
                    <div className="os-drop-progress">
                      <div className="os-progress-header">
                        <span className="os-progress-text">
                          {drop.mintedAmount.toLocaleString()} / {drop.maxSupply.toLocaleString()} minted
                        </span>
                        <span className="os-progress-percent">{progress.toFixed(0)}%</span>
                      </div>
                      <div className="os-progress-bar">
                        <div className="os-progress-fill" style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>

                    {/* Price & CTA */}
                    <div className="os-drop-footer">
                      <div className="os-drop-price">
                        <span className="os-price-label">Mint Price</span>
                        <span className="os-price-value">
                          {drop.pricePerNft.toLocaleString()} {drop.currency}
                        </span>
                      </div>
                      <button
                        className={`os-mint-btn ${status.status === 'live' ? 'primary' : ''}`}
                        disabled={status.status === 'minted' || status.status === 'ended'}
                      >
                        {status.status === 'live' ? 'Mint Now' :
                         status.status === 'allowlist' ? 'Join Allowlist' :
                         status.status === 'upcoming' ? 'Notify Me' :
                         'View Collection'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="os-empty-state">
            <FaRocket />
            <h3>No drops available</h3>
            <p>Check back soon for new drops</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedDrop && (
        <div className="os-modal-overlay">
          <div className="os-modal" ref={modalRef}>
            {/* Modal Close */}
            <button className="os-modal-close" onClick={closeModal}>
              <FaTimes />
            </button>

            {/* Modal Banner */}
            <div className="os-modal-banner">
              <img src={selectedDrop.bannerImage} alt="" />
              <div className="os-modal-banner-fade"></div>
            </div>

            {/* Modal Content */}
            <div className="os-modal-body">
              {/* Header */}
              <div className="os-modal-header">
                <div className="os-modal-logo">
                  <img src={selectedDrop.logoImage} alt={selectedDrop.name} />
                </div>
                <div className="os-modal-info">
                  <div className="os-modal-creator">
                    <img src={selectedDrop.creator.image} alt="" />
                    <span>{selectedDrop.creator.name}</span>
                    {selectedDrop.creator.verified && <FaCheckCircle className="os-verified-icon" />}
                  </div>
                  <h2 className="os-modal-title">{selectedDrop.name}</h2>
                  <div className="os-modal-links">
                    {selectedDrop.website && (
                      <a href={selectedDrop.website} target="_blank" rel="noopener noreferrer"><FaGlobe /></a>
                    )}
                    {selectedDrop.twitter && (
                      <a href={selectedDrop.twitter} target="_blank" rel="noopener noreferrer"><FaTwitter /></a>
                    )}
                    {selectedDrop.discord && (
                      <a href={selectedDrop.discord} target="_blank" rel="noopener noreferrer"><FaDiscord /></a>
                    )}
                    {selectedDrop.telegram && (
                      <a href={selectedDrop.telegram} target="_blank" rel="noopener noreferrer"><FaTelegram /></a>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="os-modal-desc">{selectedDrop.description}</p>

              {/* Stats Grid */}
              <div className="os-modal-stats">
                <div className="os-modal-stat">
                  <span className="os-modal-stat-value">{selectedDrop.maxSupply.toLocaleString()}</span>
                  <span className="os-modal-stat-label">Total Supply</span>
                </div>
                <div className="os-modal-stat">
                  <span className="os-modal-stat-value">{selectedDrop.mintedAmount.toLocaleString()}</span>
                  <span className="os-modal-stat-label">Minted</span>
                </div>
                <div className="os-modal-stat">
                  <span className="os-modal-stat-value">{selectedDrop.stats.owners.toLocaleString()}</span>
                  <span className="os-modal-stat-label">Owners</span>
                </div>
                <div className="os-modal-stat">
                  <span className="os-modal-stat-value">{selectedDrop.stats.floorPrice}</span>
                  <span className="os-modal-stat-label">Floor</span>
                </div>
              </div>

              {/* Countdown */}
              {(() => {
                const status = getDropStatus(selectedDrop);
                if (status.countdown) {
                  return (
                    <div className="os-modal-countdown-box">
                      <span className="os-modal-countdown-title">
                        {status.status === 'upcoming' ? 'Drop starts in' :
                         status.status === 'allowlist' ? 'Allowlist ends in' : 'Drop ends in'}
                      </span>
                      {renderCountdown(status.countdown)}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Stages */}
              <div className="os-modal-stages">
                <h4>Mint Stages</h4>
                {selectedDrop.stages.map((stage, idx) => (
                  <div key={idx} className="os-stage-row">
                    <div className="os-stage-info">
                      <span className="os-stage-name">{stage.name}</span>
                      {stage.maxPerWallet && (
                        <span className="os-stage-limit">{stage.maxPerWallet} per wallet</span>
                      )}
                    </div>
                    <span className="os-stage-price">{stage.price.toLocaleString()} {selectedDrop.currency}</span>
                  </div>
                ))}
              </div>

              {/* Mint Controls */}
              {(() => {
                const status = getDropStatus(selectedDrop);
                const canMint = status.status === 'live';
                const totalPrice = getTotalPrice();
                const insufficientBalance = isConnected && userBalance < totalPrice;

                return (
                  <div className="os-modal-mint-section">
                    {/* Quantity Selector */}
                    <div className="os-quantity-row">
                      <span className="os-quantity-label">Quantity</span>
                      <div className="os-quantity-controls">
                        <button
                          onClick={() => setMintAmount(Math.max(1, mintAmount - 1))}
                          disabled={mintAmount <= 1}
                        >
                          <FaMinus />
                        </button>
                        <span className="os-quantity-value">{mintAmount}</span>
                        <button
                          onClick={() => setMintAmount(Math.min(10, mintAmount + 1))}
                          disabled={mintAmount >= 10}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="os-total-row">
                      <span>Total</span>
                      <span className="os-total-price">{totalPrice.toLocaleString()} {selectedDrop.currency}</span>
                    </div>

                    {/* Balance */}
                    {isConnected && (
                      <div className="os-balance-row">
                        <span>Your balance</span>
                        <span className={insufficientBalance ? 'os-insufficient' : ''}>
                          {userBalance.toLocaleString()} {selectedDrop.currency}
                        </span>
                      </div>
                    )}

                    {/* Transaction Status */}
                    {transactionMessage && (
                      <div className={`os-tx-status ${transactionState}`}>
                        {transactionState === 'processing' && <div className="os-spinner"></div>}
                        {transactionState === 'success' && <FaCheckCircle />}
                        {transactionState === 'error' && <FaTimesCircle />}
                        <span>{transactionMessage}</span>
                      </div>
                    )}

                    {/* Mint Button */}
                    <button
                      className="os-modal-mint-btn"
                      onClick={isConnected ? handleMint : connectWallet}
                      disabled={
                        transactionState === 'processing' ||
                        (isConnected && !canMint) ||
                        (isConnected && insufficientBalance)
                      }
                    >
                      {transactionState === 'processing' ? (
                        'Processing...'
                      ) : !isConnected ? (
                        <>
                          <FaWallet /> Connect Wallet
                        </>
                      ) : !canMint ? (
                        status.status === 'upcoming' ? 'Not Started Yet' : 'Mint Ended'
                      ) : insufficientBalance ? (
                        'Insufficient Balance'
                      ) : (
                        `Mint for ${totalPrice.toLocaleString()} ${selectedDrop.currency}`
                      )}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropsPage;
